import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Box, Fab, Paper, IconButton, Typography, TextField,
    Badge, CircularProgress, Tooltip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import {
    AccessTime, Circle, Delete as DeleteIcon,
    Error as ErrorIcon, Replay, AddPhotoAlternateRounded, SendRounded
} from "@mui/icons-material";
import { ActivationState } from "@stomp/stompjs";
import { useWebSocket, APP_NAME } from "../websocket";
import { playMessageReceived } from "../utils";
import { ws_getMessages, ws_markRead } from "../../network/api/ws.api";
import { media_AddImage } from "../../network/api/multimedia.api";
import { ErrorContext } from "../../components/GympinPagesProvider";

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

function shouldShowMessage(msg, myApp) {
    return (msg?.AppName ?? "CLIENT") === myApp;
}

const ChatWidget = () => {
    const currentUser = useSelector((state) => state.auth.user);
    const token = useSelector((state) => state.auth.token);
    const myUserId = currentUser?.Id || currentUser?.id;
    const error = useContext(ErrorContext);

    const { status, reactive, sendChatMessage, addChatListener } = useWebSocket();

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState([]);
    const [pendingMessages, setPendingMessages] = useState([]);
    const [uploading, setUploading] = useState(false);

    const msgBox = useRef(null);
    const fileInputRef = useRef(null);

    const handleMarkRead = useCallback(() => {
        if (!myUserId) return;
        ws_markRead(myUserId, APP_NAME).catch(() => {});
    }, [myUserId]);

    const changeMessages = useCallback((msg) => {
        if (!shouldShowMessage(msg, APP_NAME)) return;
        setMessages((prev) => [...prev, msg]);
        if (msg?.Sender === "ADMIN") {
            setOpen(true);
            handleMarkRead();
        }
        playMessageReceived(msg?.Sender);
        setPendingMessages((prev) =>
            prev.filter((pm) => (msg.Id && pm.Id === msg.Id) || (pm.Message === msg.Message && pm.Sender === msg.Sender))
        );
    }, [handleMarkRead]);

    useEffect(() => {
        const unsubscribe = addChatListener((msg) => {
            changeMessages(msg);
        });
        return unsubscribe;
    }, [addChatListener, changeMessages]);

    const handleMessageStatus = useCallback(({ message, status: msgStatus }) => {
        if (msgStatus === "failed") {
            setPendingMessages((prev) =>
                prev.map((pm) => (pm.id === message.id ? { ...pm, failed: true } : pm))
            );
        }
    }, []);

    useEffect(() => {
        if (msgBox.current) {
            msgBox.current.scrollTop = msgBox.current.scrollHeight;
        }
    }, [messages, pendingMessages, open]);

    useEffect(() => {
        if (!myUserId) return;
        ws_getMessages(myUserId, APP_NAME)
            .then((result) => {
                const data = result.data?.Data;
                const list = Array.isArray(data)
                    ? data
                    : (Array.isArray(data?.content) ? data.content : []);
                setMessages(list);
            })
            .catch((e) => {
                const msg = e?.response?.data?.Message || "خطا در دریافت سابقه پیام‌ها";
                error?.showError?.({ message: msg });
            });
    }, [myUserId, error]);

    const toggleChat = () => {
        const nextState = !open;
        setOpen(nextState);
        if (nextState) {
            handleMarkRead();
        }
    };

    const handleSendMessage = useCallback(() => {
        if (input.trim() && myUserId) {
            const clientMsgId = Date.now();
            const payload = { Message: input.trim(), Sender: "USER", id: clientMsgId };
            setPendingMessages((prev) => [...prev, payload]);
            sendChatMessage(payload, handleMessageStatus);
            setInput("");
        }
    }, [input, myUserId, sendChatMessage, handleMessageStatus]);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file || !myUserId) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            error?.showError?.({ message: "تنها تصاویر با فرمت PNG و JPG مجاز هستند." });
            e.target.value = "";
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            error?.showError?.({ message: "حجم تصویر نباید بیشتر از ۵ مگابایت باشد." });
            e.target.value = "";
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append("MediaType", "IMAGE");
        formData.append("File", file);
        formData.append("Title", "chat_attachment");

        media_AddImage(formData)
            .then((res) => {
                const mediaData = res.data?.Data;
                const clientMsgId = Date.now();
                const payload = {
                    Message: input.trim(),
                    Sender: "USER",
                    MultimediaId: mediaData?.Id,
                    id: clientMsgId,
                };
                const pending = {
                    ...payload,
                    Multimedia: mediaData,
                };
                setPendingMessages((prev) => [...prev, pending]);
                sendChatMessage(payload, handleMessageStatus);
                setInput("");
            })
            .catch((err) => {
                const msg = err?.response?.data?.Message || "خطا در آپلود تصویر";
                error?.showError?.({ message: msg });
            })
            .finally(() => {
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            });
    };

    const deletePendingMessage = useCallback((msg) => () => {
        setPendingMessages((prev) => prev.filter((pm) => pm.id !== msg.id));
    }, []);

    const resendPendingMessage = useCallback((msg) => () => {
        setPendingMessages((prev) =>
            prev.map((pm) => (pm.id === msg.id ? { ...pm, failed: false } : pm))
        );
        sendChatMessage(
            { Message: msg.Message, Sender: "USER", MultimediaId: msg.MultimediaId, id: msg.id },
            handleMessageStatus
        );
    }, [sendChatMessage, handleMessageStatus]);

    if (!token || !myUserId) return null;

    const unreadCount = messages.filter((m) => m?.Sender === "ADMIN" && !m?.IsRead).length;
    const isConnected = status === ActivationState.ACTIVE;
    const allMessages = [...messages, ...pendingMessages];

    return (
        <>
            <Fab
                color="primary"
                onClick={toggleChat}
                sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}
            >
                <Badge badgeContent={unreadCount} color="error">
                    <ChatIcon />
                </Badge>
            </Fab>

            {open && (
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        borderRadius: 3,
                        bottom: 80,
                        right: 24,
                        width: { xs: "calc(100vw - 32px)", sm: 360 },
                        maxWidth: 360,
                        height: 480,
                        display: "flex",
                        flexDirection: "column",
                        zIndex: 1000,
                        overflow: "hidden",
                        bgcolor: "background.paper",
                        color: "text.primary",
                        border: 1,
                        borderColor: "divider",
                    }}
                >
                    <Box
                        sx={{
                            px: 1.5,
                            py: 1.25,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            borderBottom: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                            <Circle
                                sx={{
                                    fontSize: 12,
                                    color: isConnected ? "success.light" : "warning.light",
                                }}
                            />
                            {isConnected ? (
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "inherit" }}>
                                    پشتیبانی آنلاین
                                </Typography>
                            ) : (
                                <Typography variant="caption" sx={{ color: "inherit" }}>
                                    درحال اتصال...{" "}
                                    <Box
                                        component="span"
                                        onClick={reactive}
                                        sx={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontWeight: 700,
                                        }}
                                    >
                                        تلاش مجدد
                                    </Box>
                                </Typography>
                            )}
                        </Box>

                        <IconButton size="small" onClick={toggleChat} sx={{ color: "inherit" }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box
                        ref={msgBox}
                        sx={{
                            flex: 1,
                            p: 1.5,
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            bgcolor: "action.hover",
                        }}
                    >
                        {allMessages.map((msg, i) => {
                            const isUser = msg?.Sender === "USER" || msg?.Sender === "Client";
                            const isPending = pendingMessages.includes(msg);
                            return (
                                <Box
                                    key={msg.Id || msg.id || `m-${i}`}
                                    sx={{
                                        alignSelf: isUser ? "flex-start" : "flex-end",
                                        bgcolor: msg.failed
                                            ? "error.dark"
                                            : isUser
                                                ? "primary.main"
                                                : "action.selected",
                                        color: msg.failed || isUser
                                            ? "primary.contrastText"
                                            : "text.primary",
                                        p: 1.1,
                                        borderRadius: 2,
                                        maxWidth: "85%",
                                        wordBreak: "break-word",
                                        border: 1,
                                        borderColor: "divider",
                                    }}
                                >
                                    {msg.Multimedia?.Url && (
                                        <Box
                                            component="img"
                                            src={msg.Multimedia.Url}
                                            alt="تصویر ارسالی"
                                            onClick={() => window.open(msg.Multimedia.Url, "_blank")}
                                            sx={{
                                                maxWidth: "100%",
                                                maxHeight: 160,
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                display: "block",
                                                mb: msg.Message ? 0.5 : 0,
                                                bgcolor: "#000",
                                            }}
                                        />
                                    )}

                                    {msg?.Message && (
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: "pre-wrap", color: "inherit", lineHeight: 1.55 }}
                                        >
                                            {msg.Message}
                                        </Typography>
                                    )}

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 0.5,
                                            mt: 0.5,
                                            opacity: 0.85,
                                        }}
                                    >
                                        {isPending && !msg.failed && (
                                            <AccessTime sx={{ fontSize: 13 }} />
                                        )}
                                        {msg?.failed && (
                                            <>
                                                <ErrorIcon sx={{ fontSize: 14, color: "error.light" }} />
                                                <Tooltip title="ارسال مجدد">
                                                    <Replay
                                                        sx={{ fontSize: 14, cursor: "pointer", color: "error.light" }}
                                                        onClick={resendPendingMessage(msg)}
                                                    />
                                                </Tooltip>
                                                <Tooltip title="حذف">
                                                    <DeleteIcon
                                                        sx={{ fontSize: 14, cursor: "pointer", color: "error.light" }}
                                                        onClick={deletePendingMessage(msg)}
                                                    />
                                                </Tooltip>
                                            </>
                                        )}
                                        {msg?.CreatedDate && (
                                            <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "inherit" }}>
                                                {new Date(msg.CreatedDate).toLocaleTimeString("fa-IR", {
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            p: 1,
                            gap: 0.75,
                            bgcolor: "background.paper",
                            borderTop: 1,
                            borderColor: "divider",
                            alignItems: "flex-end",
                        }}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/png, image/jpeg, image/jpg"
                            style={{ display: "none" }}
                            onChange={handleFileSelect}
                        />

                        <Tooltip title="ارسال تصویر (PNG / JPG حداکثر ۵ مگابایت)">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={uploading}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {uploading ? <CircularProgress size={20} /> : <AddPhotoAlternateRounded />}
                                </IconButton>
                            </span>
                        </Tooltip>

                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={input}
                            multiline
                            maxRows={3}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="پیام خود را بنویسید..."
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                        />

                        <IconButton
                            color="primary"
                            disabled={!input.trim()}
                            onClick={handleSendMessage}
                        >
                            <SendRounded />
                        </IconButton>
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default ChatWidget;
